import type { BackendClient } from '../../bridge/backend-client';
import type { DecodeOptions } from '../../../core/media/snapshot-decode';

/*
 * CameraStreamManager — the main-thread MEDIA plane for live camera feeds. The
 * WebRTC RTCPeerConnection is main-thread-only (it can't live in the worker), so
 * this is the one place the frontend owns network IO outside the Backend Worker.
 * Only the SDP signaling round-trip crosses the worker (Video.WatchCamera, session-
 * authed + org-scoped); the DTLS-SRTP media flows browser↔SFU out-of-band.
 *
 * World-class lifecycle, shared across EVERY view (detail page, dashboard widget,
 * any future surface) — the discipline a YouTube/Netflix client applies:
 *
 *  • ONE stream per camera, refcounted. N views of the same camera share ONE
 *    RTCPeerConnection + ONE MediaStream. Opening twice is free; the second
 *    viewer just attaches.
 *  • Look-away pause. A shared IntersectionObserver gates liveness on whether ANY
 *    view of the camera is on-screen; Page Visibility gates on the tab. When no
 *    view can see it, the PC closes (drops the SFU viewer → the cloud cascade
 *    tears down on-demand → zero wasted media + bandwidth). It re-establishes the
 *    instant a view comes back.
 *  • Grace before close. A brief hold after the last view hides absorbs nav
 *    thrash (detail→home→detail) + momentary scroll without re-handshaking.
 *  • Self-healing. ICE 'failed'/'disconnected', a frozen-but-connected stall
 *    (framesDecoded watchdog), and signaling errors all converge on a backoff
 *    re-handshake — which re-races edge↔cloud transport in the worker, so a path
 *    that dies mid-stream fails over to the surviving one.
 *  • Fresh auth every handshake. The worker injects a fresh session JWT per
 *    WatchCamera call (with unauthenticated-retry); media needs no token once
 *    DTLS is up, so "renewal" is just the reconnect handshake re-authing.
 *
 * ICE: no iceServers. The SFU advertises its reachable host candidate (LAN IP on
 * the edge, the resolved cloud public IP on the relay); the browser punches out
 * to it and the SFU learns the browser's reflexive address from that binding
 * check (peer-reflexive) — so both LAN and cloud connect with zero STUN/TURN.
 * TURN-from-`state.cloud.turn` is a future fallback for symmetric-NAT clients.
 */

export type CameraStreamState = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'error';

const CAM_LOG = true; // DIAGNOSTIC: lifecycle tracing to find the slow/flaky step
const GRACE_CLOSE_MS = 2_000; // hold the PC after a view HIDES (grid scroll) — short, so
// off-screen grid feeds don't stay live. The longer KEEP_WARM_MS applies only when the
// LAST lease is released (nav away), so navigating away and back is instant (the PC is
// still up → no re-handshake, no waiting for the camera's next keyframe).
const KEEP_WARM_MS = 30_000;
const ICE_GATHER_TIMEOUT_MS = 600; // non-trickle cap; LAN host candidates resolve in tens of ms
// Target jitter-buffer depth (ms). WebRTC defaults to ~tens of ms tuned for two-way
// CALLS where latency is king; a one-way camera feed is latency-tolerant, so we
// buffer a bit more — enough to absorb the link's normal jitter (so packets aren't
// counted "late" → lost) AND give NACK a few retransmit round-trips to recover a
// real loss before the frame is shown. The camera can't emit an on-demand keyframe,
// so keeping loss from ever reaching the decoder is what prevents a freeze. 100ms is
// the low-latency middle ground (still feels live); raise toward 250–500ms if a
// jittery link still freezes, lower for snappier at the cost of less absorption.
const JITTER_BUFFER_MS = 100;
const STALL_CHECK_MS = 3_000; // framesDecoded watchdog cadence
const STALL_TIMEOUT_MS = 6_000; // connected-but-frozen this long ⇒ re-handshake
const DISCONNECT_GRACE_MS = 4_000; // let ICE self-recover before a full re-handshake
const BACKOFF_BASE_MS = 500;
const BACKOFF_MAX_MS = 15_000;

function streamKey(gatewayId: string, cameraId: string): string {
  return `${gatewayId}\0${cameraId}`;
}

/** A view's lease on a shared camera stream. Acquire one per view; `observe()` the
 *  element whose visibility should gate the live media; `release()` on teardown. */
export interface CameraStreamHandle {
  readonly gatewayId: string;
  readonly cameraId: string;
  /** The shared MediaStream once media is flowing; null while idle/paused. */
  readonly stream: MediaStream | null;
  readonly state: CameraStreamState;
  /** Live receive bitrate (kbps) and transport RTT (ms), refreshed ~every 3s while
   *  live; 0 when not yet measured. For the on-feed status badges. */
  readonly bitrateKbps: number;
  readonly rttMs: number;
  /** Gate liveness on this element being on-screen (typically the view's host). */
  observe(el: Element): void;
  unobserve(el: Element): void;
  /** `change` fires on any state or stream transition. */
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
  release(): void;
}

export class CameraStreamManager {
  private readonly streams = new Map<string, CameraStream>();
  private readonly io: IntersectionObserver;
  private readonly elToStream = new WeakMap<Element, CameraStream>();

  constructor(private readonly backend: BackendClient) {
    this.io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) this.elToStream.get(e.target)?.markVisible(e.target, e.isIntersecting);
      },
      { threshold: 0.01 },
    );
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private readonly onVisibility = (): void => {
    for (const s of this.streams.values()) s.reconcile();
  };

  /** Acquire a (shared) live stream for one camera. Multiple acquires of the same
   *  (gateway, camera) share one PC + MediaStream. */
  acquire(gatewayId: string, cameraId: string): CameraStreamHandle {
    const key = streamKey(gatewayId, cameraId);
    let s = this.streams.get(key);
    if (!s) {
      s = new CameraStream(gatewayId, cameraId, this.backend);
      this.streams.set(key, s);
    }
    s.addLease();
    return new Handle(this, s);
  }

  /** A still POSTER for one camera — the latest live keyframe, decoded to an image
   *  object-URL (the caller revokes it). Used by the cloud grid: instead of N live
   *  WebRTC feeds (Hetzner egress), each card shows a periodically-refreshed poster
   *  (one ~tile-sized keyframe). The edge extracts the encoded keyframe; the decode
   *  runs in the worker on the viewer's own hardware (snapshotImage). */
  async snapshotUrl(gatewayId: string, cameraId: string, opts?: DecodeOptions): Promise<string> {
    const blob = await this.backend.api.snapshotImage(gatewayId, cameraId, 0n, opts);
    return URL.createObjectURL(blob);
  }

  /** Tear down everything (app shutdown). */
  dispose(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.io.disconnect();
    for (const s of this.streams.values()) s.dispose();
    this.streams.clear();
  }

  // ── Handle plumbing ──
  observeEl(s: CameraStream, el: Element): void {
    this.elToStream.set(el, s);
    this.io.observe(el);
  }
  unobserveEl(s: CameraStream, el: Element): void {
    this.io.unobserve(el);
    this.elToStream.delete(el);
    s.forgetEl(el);
  }
  releaseLease(s: CameraStream): void {
    s.removeLease();
    if (s.leaseCount === 0) {
      // GC the map entry only AFTER the keep-warm window — so a return navigation
      // within it re-acquires the still-warm stream (instant) rather than a cold one.
      window.setTimeout(() => {
        if (s.leaseCount === 0 && s.isFullyIdle) {
          this.streams.delete(streamKey(s.gatewayId, s.cameraId));
          s.dispose();
        }
      }, KEEP_WARM_MS + 500);
    }
  }
}

class Handle implements CameraStreamHandle {
  private readonly els = new Set<Element>();
  private released = false;

  constructor(
    private readonly mgr: CameraStreamManager,
    private readonly s: CameraStream,
  ) {}

  get gatewayId(): string {
    return this.s.gatewayId;
  }
  get cameraId(): string {
    return this.s.cameraId;
  }
  get stream(): MediaStream | null {
    return this.s.stream;
  }
  get state(): CameraStreamState {
    return this.s.state;
  }
  get bitrateKbps(): number {
    return this.s.bitrateKbps;
  }
  get rttMs(): number {
    return this.s.rttMs;
  }

  observe(el: Element): void {
    if (this.released || this.els.has(el)) return;
    this.els.add(el);
    this.mgr.observeEl(this.s, el);
  }
  unobserve(el: Element): void {
    if (!this.els.delete(el)) return;
    this.mgr.unobserveEl(this.s, el);
  }
  addEventListener(type: 'change', listener: () => void): void {
    this.s.addEventListener(type, listener);
  }
  removeEventListener(type: 'change', listener: () => void): void {
    this.s.removeEventListener(type, listener);
  }
  release(): void {
    if (this.released) return;
    this.released = true;
    for (const el of this.els) this.mgr.unobserveEl(this.s, el);
    this.els.clear();
    this.mgr.releaseLease(this.s);
  }
}

/*
 * One shared, refcounted camera stream. Owns the RTCPeerConnection + its state
 * machine. `desired` = (leased) && (some view visible) && (tab visible); reconcile
 * opens/closes the PC to match, with a grace hold on close and backoff on retry.
 * A generation counter invalidates every in-flight async step on any (re)open or
 * teardown, so stale handshakes never clobber a newer one.
 */
class CameraStream extends EventTarget {
  state: CameraStreamState = 'idle';
  stream: MediaStream | null = null;

  private leases = 0;
  private readonly visibleEls = new Set<Element>();
  private pc?: RTCPeerConnection;
  private gen = 0;
  private retryAttempts = 0;
  private retryTimer?: number;
  private closeTimer?: number;
  private statsTimer?: number;
  private disconnectTimer?: number;
  private lastFrames = -1;
  private lastFramesAt = 0;
  private lastPacketsLost = -1; // DIAGNOSTIC: delta-loss trend
  // Live receive stats for the on-feed badges (refreshed by the stall poll).
  bitrateKbps = 0;
  rttMs = 0;
  private lastBytes = -1;
  private lastBytesAt = 0;

  constructor(
    readonly gatewayId: string,
    readonly cameraId: string,
    private readonly backend: BackendClient,
  ) {
    super();
  }

  get leaseCount(): number {
    return this.leases;
  }
  /** No PC, no pending retry, not desired — safe to GC. */
  get isFullyIdle(): boolean {
    return !this.pc && this.retryTimer === undefined && !this.desired();
  }

  addLease(): void {
    this.leases++;
    this.reconcile();
  }
  removeLease(): void {
    this.leases = Math.max(0, this.leases - 1);
    this.reconcile();
  }
  markVisible(el: Element, visible: boolean): void {
    if (visible) this.visibleEls.add(el);
    else this.visibleEls.delete(el);
    this.log(`visible=${visible} (visibleEls=${this.visibleEls.size} leases=${this.leases} desired=${this.desired()})`);
    this.reconcile();
  }
  forgetEl(el: Element): void {
    this.visibleEls.delete(el);
    this.reconcile();
  }

  private desired(): boolean {
    return this.leases > 0 && this.visibleEls.size > 0 && document.visibilityState === 'visible';
  }

  reconcile(): void {
    if (this.desired()) {
      if (this.closeTimer !== undefined) {
        clearTimeout(this.closeTimer);
        this.closeTimer = undefined;
      }
      const settling = this.state === 'connecting' || this.state === 'reconnecting';
      if (!this.pc && this.retryTimer === undefined && !settling) void this.open();
    } else if (this.pc && this.closeTimer === undefined) {
      // Not desired → graceful close (cancelled above if it becomes desired again).
      // Keep the PC warm much longer when fully UNLEASED (nav away) than when merely
      // hidden-but-leased (grid scroll), so a return navigation reuses the live PC.
      const grace = this.leases === 0 ? KEEP_WARM_MS : GRACE_CLOSE_MS;
      this.closeTimer = window.setTimeout(() => {
        this.closeTimer = undefined;
        if (!this.desired()) this.teardown('idle');
      }, grace);
    } else if (!this.pc && this.retryTimer !== undefined && !this.desired()) {
      // A backoff retry was queued but we're no longer wanted — drop it.
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
      this.setState('idle');
    }
  }

  private log(msg: string): void {
    if (CAM_LOG) console.log(`[cam ${this.cameraId.slice(0, 12)}] ${msg}`);
  }

  private async open(): Promise<void> {
    this.teardownPC();
    const myGen = ++this.gen;
    this.setState(this.stream ? 'reconnecting' : 'connecting');
    const t0 = Date.now();
    this.log(`open (${this.state})`);

    const pc = new RTCPeerConnection({ bundlePolicy: 'max-bundle' });
    this.pc = pc;
    const ms = new MediaStream();
    pc.addTransceiver('video', { direction: 'recvonly' });

    pc.ontrack = (e) => {
      if (this.gen !== myGen) return;
      this.log(`media track +${Date.now() - t0}ms`);
      // Buffer like a player, not a call (see JITTER_BUFFER_MS) — absorb jitter +
      // give NACK time to recover. `jitterBufferTarget` is Chromium 114+; the cast
      // is because it's newer than the lib DOM types. No-op where unsupported.
      try {
        const rcv = e.receiver as RTCRtpReceiver & { jitterBufferTarget?: number | null };
        if ('jitterBufferTarget' in rcv) rcv.jitterBufferTarget = JITTER_BUFFER_MS;
      } catch {
        /* unsupported — fall back to the browser default */
      }
      ms.addTrack(e.track);
      this.setStream(ms); // component swaps video.srcObject; new media replaces the frozen frame
    };
    pc.oniceconnectionstatechange = () => {
      if (this.gen !== myGen) return;
      this.log(`ice=${pc.iceConnectionState} +${Date.now() - t0}ms`);
      switch (pc.iceConnectionState) {
        case 'connected':
        case 'completed':
          this.clearDisconnectGrace();
          this.retryAttempts = 0;
          this.setState('live');
          this.startStallWatch(myGen);
          break;
        case 'disconnected':
          this.setState('reconnecting');
          this.armDisconnectGrace();
          break;
        case 'failed':
          this.scheduleRetry();
          break;
        // 'new' | 'checking' | 'closed' — no action
      }
    };

    try {
      const offer = await pc.createOffer();
      if (this.gen !== myGen) return void pc.close();
      await pc.setLocalDescription(offer);
      await this.waitIceGathering(pc);
      this.log(`gathered +${Date.now() - t0}ms (state=${pc.iceGatheringState})`);
      if (this.gen !== myGen) return void pc.close();
      const tRpc = Date.now();
      const sdpAnswer = await this.backend.api.watchCamera(this.gatewayId, this.cameraId, pc.localDescription!.sdp);
      this.log(`watchCamera ok +${Date.now() - t0}ms (rpc ${Date.now() - tRpc}ms)`);
      if (this.gen !== myGen) return void pc.close();
      await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
      // 'live' arrives via oniceconnectionstatechange
    } catch (err) {
      if (this.gen !== myGen) return;
      this.log(`handshake ERROR +${Date.now() - t0}ms: ${err instanceof Error ? err.message : String(err)}`);
      this.scheduleRetry();
    }
  }

  private waitIceGathering(pc: RTCPeerConnection): Promise<void> {
    if (pc.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve) => {
      const finish = (): void => {
        clearTimeout(timer);
        pc.removeEventListener('icegatheringstatechange', onChange);
        resolve();
      };
      const onChange = (): void => {
        if (pc.iceGatheringState === 'complete') finish();
      };
      const timer = window.setTimeout(finish, ICE_GATHER_TIMEOUT_MS);
      pc.addEventListener('icegatheringstatechange', onChange);
    });
  }

  private armDisconnectGrace(): void {
    this.clearDisconnectGrace();
    this.disconnectTimer = window.setTimeout(() => {
      this.disconnectTimer = undefined;
      // Still not recovered → full re-handshake (which re-races edge↔cloud).
      if (this.pc && this.pc.iceConnectionState !== 'connected' && this.pc.iceConnectionState !== 'completed') {
        this.scheduleRetry();
      }
    }, DISCONNECT_GRACE_MS);
  }
  private clearDisconnectGrace(): void {
    if (this.disconnectTimer !== undefined) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = undefined;
    }
  }

  private startStallWatch(myGen: number): void {
    this.stopStallWatch();
    this.lastFrames = -1;
    this.lastFramesAt = Date.now();
    this.lastPacketsLost = -1;
    this.lastBytes = -1;
    this.statsTimer = window.setInterval(() => void this.checkStall(myGen), STALL_CHECK_MS);
  }
  private stopStallWatch(): void {
    if (this.statsTimer !== undefined) {
      clearInterval(this.statsTimer);
      this.statsTimer = undefined;
    }
    if (this.bitrateKbps !== 0 || this.rttMs !== 0) {
      this.bitrateKbps = 0;
      this.rttMs = 0;
    }
  }
  private async checkStall(myGen: number): Promise<void> {
    if (this.gen !== myGen || !this.pc) return;
    let frames = -1;
    let inbound: Record<string, number> | undefined;
    let rttSec = 0;
    try {
      const stats = await this.pc.getStats();
      stats.forEach((r) => {
        const rec = r as { type: string; kind?: string; framesDecoded?: number; nominated?: boolean; currentRoundTripTime?: number };
        if (rec.type === 'inbound-rtp' && rec.kind === 'video') {
          inbound = rec as unknown as Record<string, number>;
          if (typeof rec.framesDecoded === 'number') frames = rec.framesDecoded;
        } else if (rec.type === 'candidate-pair' && rec.nominated && typeof rec.currentRoundTripTime === 'number') {
          rttSec = rec.currentRoundTripTime; // the active transport's RTT
        }
      });
    } catch {
      return;
    }
    if (this.gen !== myGen) return;
    const now = Date.now();
    // Live bitrate (from bytesReceived delta) + transport RTT → the on-feed badges.
    if (inbound) {
      const bytes = inbound.bytesReceived ?? 0;
      if (this.lastBytes >= 0 && now > this.lastBytesAt) {
        const kbps = Math.round(((bytes - this.lastBytes) * 8) / (now - this.lastBytesAt));
        const rtt = rttSec > 0 ? Math.round(rttSec * 1000) : this.rttMs;
        if (kbps !== this.bitrateKbps || rtt !== this.rttMs) {
          this.bitrateKbps = Math.max(0, kbps);
          this.rttMs = rtt;
          this.emitChange();
        }
      }
      this.lastBytes = bytes;
      this.lastBytesAt = now;
    }
    // DIAGNOSTIC: surface WHAT degrades — loss (lost+), network jitter, decode drops,
    // perceived freezes, how many NACKs/PLIs the browser is sending (pli>0 means it's
    // begging for a keyframe the camera can't make), and the jitter-buffer latency.
    if (inbound) {
      const lost = inbound.packetsLost ?? 0;
      const dLost = this.lastPacketsLost >= 0 ? lost - this.lastPacketsLost : 0;
      this.lastPacketsLost = lost;
      const jbuf =
        inbound.jitterBufferDelay && inbound.jitterBufferEmittedCount
          ? Math.round((inbound.jitterBufferDelay / inbound.jitterBufferEmittedCount) * 1000)
          : 0;
      this.log(
        `stats lost+${dLost} (tot ${lost}) jitter=${Math.round((inbound.jitter ?? 0) * 1000)}ms ` +
          `drop=${inbound.framesDropped ?? 0} freeze=${inbound.freezeCount ?? 0} ` +
          `nack=${inbound.nackCount ?? 0} pli=${inbound.pliCount ?? 0} jbuf=${jbuf}ms`,
      );
    }
    if (frames > this.lastFrames) {
      this.lastFrames = frames;
      this.lastFramesAt = now;
      return;
    }
    // Connected but no new frames for too long → frozen; re-handshake.
    if (this.lastFrames >= 0 && now - this.lastFramesAt > STALL_TIMEOUT_MS) {
      this.log(`stall (frames=${frames}, ${now - this.lastFramesAt}ms) → retry`);
      this.scheduleRetry();
    }
  }

  private scheduleRetry(): void {
    this.teardownPC();
    if (!this.desired()) {
      this.log('retry skipped — not desired');
      this.setState('idle');
      this.setStream(null);
      return;
    }
    this.setState('error');
    const delay = Math.min(BACKOFF_BASE_MS * 2 ** this.retryAttempts, BACKOFF_MAX_MS);
    this.log(`retry in ${delay}ms (attempt ${this.retryAttempts + 1})`);
    this.retryAttempts++;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = undefined;
      if (this.desired()) void this.open();
      else this.setState('idle');
    }, delay);
  }

  /** Close the PC + its watchdogs. Keeps `stream` (the frozen last frame) so a
   *  reconnect doesn't black-flash; teardown('idle') is what drops the stream. */
  private teardownPC(): void {
    this.stopStallWatch();
    this.clearDisconnectGrace();
    if (this.pc) {
      this.pc.ontrack = null;
      this.pc.oniceconnectionstatechange = null;
      try {
        this.pc.close();
      } catch {
        /* already closing */
      }
      this.pc = undefined;
    }
  }

  private teardown(next: CameraStreamState): void {
    this.gen++; // invalidate any in-flight open
    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    if (this.closeTimer !== undefined) {
      clearTimeout(this.closeTimer);
      this.closeTimer = undefined;
    }
    this.teardownPC();
    if (next === 'idle') this.setStream(null); // paused → poster, not a frozen frame
    this.setState(next);
  }

  dispose(): void {
    this.teardown('idle');
  }

  private setState(next: CameraStreamState): void {
    if (this.state === next) return;
    this.state = next;
    this.emitChange();
  }
  private setStream(next: MediaStream | null): void {
    if (this.stream === next) return;
    this.stream = next;
    this.emitChange();
  }
  private emitChange(): void {
    this.dispatchEvent(new CustomEvent('change'));
  }
}
